import { Fragment, useCallback, useEffect, useMemo, useState, type MouseEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import {
  Alert,
  Anchor,
  Badge,
  Button,
  Container,
  Group,
  Loader,
  Pagination,
  SegmentedControl,
  Select,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { IconSearch } from '@tabler/icons-react';

import { LogoPaper } from '@/components';
import { CountryPill } from '@/components/CountryPill';
import { RegistryOrderCloseModal } from '@/components/Modals/RegistryOrderCloseModal';
import { RegistryOrderQuoteModal } from '@/components/Modals/RegistryOrderQuoteModal';
import {
  RegistryInboundMailSection,
  RegistryOrderPanel,
  RegistryOrderReplyBadge,
} from '@/components/RegistryOrders';
import { StatusBadge } from '@/components/StatusBadge';
import { useRegistryAttention, useTeams } from '@/hooks';
import {
  ARMADILLO_ASIDE,
  armadilloAsideSearch,
  armadilloReplyTargetKey,
} from '@/hooks/useArmadilloAsideLink';
import { useRegistryOrderCloseDialog } from '@/hooks/useRegistryOrderCloseDialog';
import { useRegistryOrderQueue } from '@/hooks/useRegistryOrderQueue';
import { useRegistryOrderQuoteDialog } from '@/hooks/useRegistryOrderQuoteDialog';
import {
  describeRegistryOrderStatus,
  effectiveRegistryOrderStatus,
  isRegistryOrderAwaitingQuote,
  isRegistryOrderLate,
  isReportActivatedForOrders,
  mayHaveRegistryDocuments,
  REGISTRY_ORDER_STATUS_META,
  registryOrderCountry,
  registryOrderReasonLine,
  type RegistryOrderDetail,
  type RegistryOrderMessage,
  type RegistryOrderQueueRow,
  type RegistryOrderQueueView,
} from '@/types/registry-documents';
import { formatDueDate } from '@/utils/businessDays';

const PAGE_SIZE = 50;
/** The longest search the queue endpoint accepts. */
const SEARCH_MAX_LENGTH = 200;

// Module-level so the SegmentedControl gets the same array every render.
const VIEW_OPTIONS: { value: RegistryOrderQueueView; label: string }[] = [
  { value: 'outstanding', label: 'Outstanding' },
  { value: 'received', label: 'Received' },
  { value: 'all', label: 'All' },
];

/** The table's named columns. A last, unnamed one holds each row's Details
 * toggle, and an opened order spans them all. */
const COLUMNS = [
  'Report',
  'Company',
  'Documents',
  'Status',
  'Received',
  'Reference',
  'Ordered',
  'Expected',
  'Team',
] as const;

/** The narrowest the table is laid out before it scrolls sideways. Kept
 * within what the page's `xl` container leaves for it (1226px), so that on
 * a wide screen nothing scrolls and the Details column is not cut off. */
const TABLE_MIN_WIDTH = 1200;

const EMPTY_WORDS: Record<RegistryOrderQueueView, string> = {
  outstanding: 'Nothing is outstanding.',
  received: 'No documents have been received yet.',
  all: 'No registry documents have been ordered.',
};

interface QueueFilters {
  view: RegistryOrderQueueView;
  teamId: string | null;
  search: string;
  lateOnly: boolean;
}

const DEFAULT_FILTERS: QueueFilters = {
  view: 'outstanding',
  teamId: null,
  search: '',
  lateOnly: false,
};

/**
 * Admin: every registry-document order across every report, to see what is
 * still owed and what has arrived without opening each report.
 *
 * Above the orders, the mail from the research desk that reached no order
 * (`RegistryInboundMailSection`): attached to its order or dismissed from
 * here, so every recorded email ends up on an order or in front of a
 * person.
 *
 * An order is placed on its report, which every row links to. Its Details
 * open here: the documents that arrived, what the research desk wrote, and
 * the way to close an order that will never be completed.
 *
 * Paging, filtering and search all run server-side, so a filter change
 * resets to page 1. Staying on page 4 of a narrower set would show an empty
 * table that reads as "nothing ordered".
 */
export function RegistryDocumentsPage() {
  const { teams } = useTeams();
  // The page is admin-only, as the attention read is. It is the shell
  // badge's leader poll: another tab hands its value over, and in the
  // leading tab this page reads it once a minute beside the badge.
  const partnerProblem = useRegistryAttention(true)?.partnerAssignmentProblem ?? null;
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<QueueFilters>(DEFAULT_FILTERS);
  const [notice, setNotice] = useState<string | null>(null);
  const [debouncedSearch] = useDebouncedValue(filters.search, 250);

  const q = debouncedSearch.trim() || undefined;
  const {
    page: result,
    isLoading,
    error,
    refetch,
    close,
    recordQuote,
  } = useRegistryOrderQueue({
    view: filters.view,
    teamId: filters.teamId ?? undefined,
    q,
    late: filters.lateOnly || undefined,
    page,
    pageSize: PAGE_SIZE,
  });

  const teamNames = useMemo(
    () => Object.fromEntries(teams.map((team) => [String(team.id), team.name])),
    [teams]
  );
  const teamOptions = useMemo(
    () =>
      teams
        .map((team) => ({ value: String(team.id), label: team.name }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [teams]
  );

  // A closed order leaves the outstanding view with the re-read that
  // follows, so the page says what became of it.
  const onClosed = useCallback((order: RegistryOrderQueueRow) => {
    setNotice(`${order.reference} was closed. It no longer counts as outstanding.`);
  }, []);
  const closeDialog = useRegistryOrderCloseDialog(close, onClosed);

  // The desk is answered in Armadillo's conversation on the order's
  // report: the Reply controls lead there, pointed at the order or the
  // email, as `threadLink` writes it.
  const navigate = useNavigate();
  const onReplyToMessage = useCallback(
    (row: RegistryOrderQueueRow, message: RegistryOrderMessage) =>
      navigate(threadLink(row, message.messageId)),
    [navigate]
  );

  // A recorded quote gives the order the date it is chased by: the page
  // says which date, since the row only shows it once the re-read lands.
  const onQuoteRecorded = useCallback(
    (order: RegistryOrderQueueRow, recorded: RegistryOrderDetail) => {
      setNotice(
        recorded.expectedAt
          ? `Quote recorded on ${order.reference}: expected by ${formatDueDate(recorded.expectedAt)}.`
          : `Quote recorded on ${order.reference}.`
      );
    },
    []
  );
  const quoteDialog = useRegistryOrderQuoteDialog(recordQuote, onQuoteRecorded);

  // An email attached from the list above lands on an order in the queue:
  // its row (the reply count, the documents) is read again at once.
  const onMailAttached = useCallback(() => void refetch(), [refetch]);

  const changeFilters = (change: Partial<QueueFilters>) => {
    setFilters((current) => ({ ...current, ...change }));
    setPage(1);
  };

  const empty = result.items.length === 0;
  const nothingInTheSet = result.total === 0;
  const narrowed = filters.teamId !== null || q !== undefined || filters.lateOnly;
  const totalPages = Math.ceil(result.total / PAGE_SIZE);

  // The set can shrink under a page that is open (orders are delivered
  // while the queue polls): step back to the last page that still exists
  // rather than show an empty page with no pager to leave it by.
  useEffect(() => {
    if (!isLoading && !error && page > 1 && page > totalPages) {
      setPage(Math.max(1, totalPages));
    }
  }, [isLoading, error, page, totalPages]);

  return (
    <Stack>
      <Container size="xl" w="100%">
        <LogoPaper>
          <Stack>
            <Stack gap={0}>
              <Title order={2}>Registry documents</Title>
              <Text size="sm" c="dimmed">
                Every Armadillo order across reports. Details shows what arrived and what the
                research desk wrote; open a report to order more.
              </Text>
            </Stack>

            {partnerProblem && (
              <Alert
                color="yellow"
                title="Armadillo is not billed automatically here"
                data-testid="registry-partner-problem"
              >
                {partnerProblem}
              </Alert>
            )}

            <RegistryInboundMailSection onAttached={onMailAttached} />

            <Title order={3}>Orders</Title>

            <QueueControls filters={filters} teamOptions={teamOptions} onChange={changeFilters} />

            {error && (
              <Alert color="red" title="Could not load the registry-document orders">
                {error.message}
              </Alert>
            )}

            {notice && (
              <Alert
                color="blue"
                withCloseButton
                onClose={() => setNotice(null)}
                data-testid="registry-queue-notice"
              >
                {notice}
              </Alert>
            )}

            {isLoading && empty && <Loader size="sm" />}

            {/* Only a set with nothing in it is "empty": a page past the
                end of a set that shrank is stepped back, not described. */}
            {!isLoading && nothingInTheSet && !error && (
              <Text c="dimmed" data-testid="registry-queue-empty">
                {narrowed ? 'No orders match these filters.' : EMPTY_WORDS[filters.view]}
              </Text>
            )}

            {!empty && (
              <QueueTable
                rows={result.items}
                teamNames={teamNames}
                onCloseOrder={closeDialog.open}
                onReplyToMessage={onReplyToMessage}
                onRecordQuote={quoteDialog.open}
              />
            )}

            {totalPages > 1 && (
              <Group justify="space-between">
                <Text size="sm" c="dimmed">
                  Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, result.total)} of{' '}
                  {result.total} orders
                </Text>
                <Pagination value={page} onChange={setPage} total={totalPages} />
              </Group>
            )}
          </Stack>
        </LogoPaper>
      </Container>
      {/* One dialog of each kind for the page rather than one per row: a
          closed order leaves the outstanding view, and its row with it;
          an answered or a quoted one is redrawn by the re-read. */}
      <RegistryOrderCloseModal {...closeDialog.modalProps} />
      <RegistryOrderQuoteModal {...quoteDialog.modalProps} />
    </Stack>
  );
}

// ---------------------------------------------------------------------------

function QueueControls({
  filters,
  teamOptions,
  onChange,
}: Readonly<{
  filters: QueueFilters;
  teamOptions: { value: string; label: string }[];
  onChange: (change: Partial<QueueFilters>) => void;
}>) {
  return (
    <Group align="flex-end">
      <SegmentedControl
        aria-label="Orders shown"
        data={VIEW_OPTIONS}
        value={filters.view}
        onChange={(value) => onChange({ view: value as RegistryOrderQueueView })}
        data-testid="registry-queue-view"
      />
      <Select
        label="Team"
        placeholder="All teams"
        data={teamOptions}
        value={filters.teamId}
        onChange={(value) => onChange({ teamId: value })}
        searchable
        clearable
      />
      <TextInput
        label="Search"
        placeholder="Company or GTI reference…"
        leftSection={<IconSearch size={14} />}
        value={filters.search}
        onChange={(e) => onChange({ search: e.currentTarget.value })}
        // The API refuses a longer search.
        maxLength={SEARCH_MAX_LENGTH}
        data-testid="registry-queue-search"
      />
      <Switch
        label="Late only"
        checked={filters.lateOnly}
        onChange={(e) => onChange({ lateOnly: e.currentTarget.checked })}
        // Lines the switch up with the inputs, which sit under a label.
        pb={8}
        data-testid="registry-queue-late"
      />
    </Group>
  );
}

function QueueTable({
  rows,
  teamNames,
  onCloseOrder,
  onReplyToMessage,
  onRecordQuote,
}: Readonly<{
  rows: RegistryOrderQueueRow[];
  teamNames: Record<string, string>;
  onCloseOrder: (row: RegistryOrderQueueRow) => void;
  onReplyToMessage: (row: RegistryOrderQueueRow, message: RegistryOrderMessage) => void;
  onRecordQuote: (row: RegistryOrderQueueRow) => void;
}>) {
  // Which orders are opened. Held here rather than in the row: an opened
  // order is a second table row, a sibling of the one that toggles it.
  const [openIds, setOpenIds] = useState<ReadonlySet<string>>(new Set());
  const toggle = (orderId: string) => {
    setOpenIds((current) => {
      const next = new Set(current);
      if (!next.delete(orderId)) {
        next.add(orderId);
      }
      return next;
    });
  };

  return (
    <Table.ScrollContainer minWidth={TABLE_MIN_WIDTH}>
      <Table striped highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            {COLUMNS.map((column) => (
              <Table.Th key={column}>{column}</Table.Th>
            ))}
            <Table.Th aria-label="Details" />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {rows.map((row) => (
            <Fragment key={row.id}>
              <QueueRow
                row={row}
                teamName={teamNames[row.teamId] ?? row.teamId}
                detailsOpen={openIds.has(row.id)}
                onToggleDetails={() => toggle(row.id)}
              />
              {openIds.has(row.id) && (
                <QueueRowDetails
                  row={row}
                  onCloseOrder={() => onCloseOrder(row)}
                  onReplyToMessage={(message) => onReplyToMessage(row, message)}
                  onRecordQuote={() => onRecordQuote(row)}
                />
              )}
            </Fragment>
          ))}
        </Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  );
}

/** A report with no codename is named by the start of its id. */
function reportName(row: RegistryOrderQueueRow): string {
  return row.reportLabel ?? `${row.reportId.slice(0, 8)}…`;
}

/** Armadillo's conversation on the order's report, pointed at the order
 * or, with a Message-ID, at one of the desk's emails on it. */
function threadLink(row: RegistryOrderQueueRow, messageId?: string | null): string {
  const target = armadilloReplyTargetKey(row.id, messageId);
  return `/reports/${row.reportId}${armadilloAsideSearch(target)}`;
}

function expectedText(row: RegistryOrderQueueRow): string {
  // A closed order expects nothing, whatever date it once had.
  if (row.status === 'cancelled') {
    return '—';
  }
  if (row.expectedAt) {
    return formatDueDate(row.expectedAt);
  }
  return isRegistryOrderAwaitingQuote(row) ? 'After quote' : '—';
}

/** Whether a click on a row means "open it". Selecting text to copy (the
 * GTI reference is what the research desk is quoted) and a modified click
 * do not. */
function isPlainClick(event: MouseEvent): boolean {
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
    return false;
  }
  return (window.getSelection()?.toString() ?? '') === '';
}

function QueueRow({
  row,
  teamName,
  detailsOpen,
  onToggleDetails,
}: Readonly<{
  row: RegistryOrderQueueRow;
  teamName: string;
  detailsOpen: boolean;
  onToggleDetails: () => void;
}>) {
  const navigate = useNavigate();
  const reportPath = `/reports/${row.reportId}`;
  const ordered = row.placedAt ?? row.createdAt;
  const counted = row.expectedDocumentCount > 0 || row.receivedDocumentCount > 0;

  return (
    <Table.Tr
      style={{ cursor: 'pointer' }}
      onClick={(event) => {
        if (isPlainClick(event)) {
          navigate(reportPath);
        }
      }}
      data-testid={`registry-queue-row-${row.id}`}
    >
      <Table.Td>
        {/* The row navigates too; the click stops here so one click is one
            history entry. */}
        <Anchor
          component={Link}
          to={reportPath}
          size="sm"
          onClick={(event) => event.stopPropagation()}
        >
          {reportName(row)}
        </Anchor>
      </Table.Td>
      <Table.Td>
        <Group gap={6} wrap="nowrap">
          <Text size="sm">{row.companyName}</Text>
          <CountryPill country={registryOrderCountry(row.countryCode)} />
          {row.manual && (
            <Tooltip label="Not in Armadillo's index: ordered by name for their research desk to identify">
              <Badge size="xs" variant="light" color="grape">
                by name
              </Badge>
            </Tooltip>
          )}
        </Group>
      </Table.Td>
      <Table.Td>
        <Text size="sm">{row.products.join(', ') || '—'}</Text>
      </Table.Td>
      <Table.Td>
        <StatusPills row={row} />
      </Table.Td>
      <Table.Td>
        <Text size="sm">
          {counted ? `${row.receivedDocumentCount}/${row.expectedDocumentCount}` : '—'}
        </Text>
      </Table.Td>
      <Table.Td>
        <Text size="sm" ff="monospace">
          {row.reference}
        </Text>
      </Table.Td>
      <Table.Td>
        <Text size="sm">{ordered ? formatDueDate(ordered) : '—'}</Text>
      </Table.Td>
      <Table.Td>
        <Text size="sm">{expectedText(row)}</Text>
      </Table.Td>
      <Table.Td>
        <Text size="sm">{teamName}</Text>
      </Table.Td>
      <Table.Td>
        <Group gap={4} wrap="nowrap">
          {/* A prepared order has never left Nexus: nothing to open. */}
          {mayHaveRegistryDocuments(row.status) && (
            <Button
              size="compact-xs"
              variant="subtle"
              // The row navigates; the toggle is not a way into the report.
              onClick={(event) => {
                event.stopPropagation();
                onToggleDetails();
              }}
              aria-expanded={detailsOpen}
              data-testid={`registry-queue-details-toggle-${row.id}`}
            >
              {detailsOpen ? 'Hide details' : 'Details'}
            </Button>
          )}
          {/* Every email on the report's orders, in Armadillo's Partners
              conversation on the report. */}
          <Anchor
            component={Link}
            to={`${reportPath}?aside=${ARMADILLO_ASIDE}`}
            size="xs"
            onClick={(event) => event.stopPropagation()}
            data-testid={`registry-queue-conversation-${row.id}`}
          >
            Conversation
          </Anchor>
        </Group>
      </Table.Td>
    </Table.Tr>
  );
}

/** An opened order: the same panel its report shows, across the table's
 * full width. Not part of the row above it, so nothing in it navigates. */
function QueueRowDetails({
  row,
  onCloseOrder,
  onReplyToMessage,
  onRecordQuote,
}: Readonly<{
  row: RegistryOrderQueueRow;
  onCloseOrder: () => void;
  onReplyToMessage: (message: RegistryOrderMessage) => void;
  onRecordQuote: () => void;
}>) {
  // Why the order ended without its documents: it failed, or a person
  // closed it. A deliberate close is not an error, so it is not red.
  const reasonLine = registryOrderReasonLine(row);

  return (
    <Table.Tr data-testid={`registry-queue-details-${row.id}`}>
      <Table.Td colSpan={COLUMNS.length + 1}>
        <Stack gap="xs" py={4}>
          {reasonLine && (
            <Text size="sm" c={row.status === 'failed' ? 'red' : 'dimmed'}>
              {reasonLine}
            </Text>
          )}
          <RegistryOrderPanel
            order={row}
            onCloseOrder={onCloseOrder}
            onReplyToMessage={onReplyToMessage}
            onRecordQuote={onRecordQuote}
          />
        </Stack>
      </Table.Td>
    </Table.Tr>
  );
}

function StatusPills({ row }: Readonly<{ row: RegistryOrderQueueRow }>) {
  // What arrived decides what is shown: the worker may not be allowed to
  // move the status itself (shadow mode).
  const status = effectiveRegistryOrderStatus(row);
  const meta = REGISTRY_ORDER_STATUS_META[status];

  return (
    <Group gap={4} wrap="nowrap">
      <StatusBadge
        label={meta.label}
        color={meta.color}
        tooltip={describeRegistryOrderStatus(
          { status, failureReason: row.failureReason },
          isReportActivatedForOrders(row.reportStage)
        )}
      />
      {isRegistryOrderLate(row) && (
        <Tooltip
          label={`Expected ${row.expectedAt ? formatDueDate(row.expectedAt) : ''}. Open Details to check for files not yet matched, then chase Armadillo's research desk quoting ${row.reference}.`}
        >
          <Badge size="sm" variant="light" color="orange">
            Late
          </Badge>
        </Tooltip>
      )}
      <RegistryOrderReplyBadge order={row} to={threadLink(row)} />
    </Group>
  );
}
